import { useCallback, useEffect, useMemo, useState } from 'react';
import { differenceInDays, format, parseISO, startOfDay } from 'date-fns';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import type { FamilyCareReviewStatus, FamilyCareReviewType } from '@/types';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppNativeSelect } from '../../../ui-foundation/adapters/web';

type AdultDependantRow = Database['public']['Tables']['adult_dependants']['Row'];
type FamilyCareReviewRow = Database['public']['Tables']['family_care_reviews']['Row'];

const REVIEW_TYPE_OPTIONS: Array<{ value: FamilyCareReviewType; label: string }> = [
  { value: 'care-plan-review', label: 'Care plan review' },
  { value: 'carers-assessment', label: 'Carer assessment' },
  { value: 'benefits-review', label: 'Benefits or funding review' },
  { value: 'medication-review', label: 'Medication review' },
  { value: 'power-of-attorney', label: 'Power of attorney admin' },
  { value: 'needs-change', label: 'Needs change check-in' },
  { value: 'other', label: 'Other review' },
];

const REMINDER_OPTIONS = [7, 14, 21, 30, 60, 90];

const STATUS_STYLES: Record<FamilyCareReviewStatus, string> = {
  open: 'bg-mustard-light text-accent',
  booked: 'bg-plum-light text-primary',
  done: 'bg-success-light text-success',
};

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function formatDate(value: string | null) {
  if (!value) return null;
  return format(parseISO(value), 'd MMM yyyy');
}

function reviewTypeLabel(value: FamilyCareReviewType) {
  return REVIEW_TYPE_OPTIONS.find((option) => option.value === value)?.label || 'Review';
}

function getReviewBadge(review: FamilyCareReviewRow) {
  const daysUntil = differenceInDays(parseISO(review.due_date), startOfDay(new Date()));
  if (review.status === 'done') {
    return { text: 'Done', classes: STATUS_STYLES.done };
  }
  if (daysUntil < 0) {
    return { text: 'Overdue', classes: 'bg-red-100 text-red-800' };
  }
  if (daysUntil <= review.reminder_days_before) {
    return {
      text: `Due in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`,
      classes: STATUS_STYLES.open,
    };
  }
  return {
    text: review.status === 'booked' ? 'Booked' : `Due ${formatDate(review.due_date)}`,
    classes: STATUS_STYLES[review.status],
  };
}

function MiniMetric({ label, value }: { label: string; value: number }) {
  return (
    <AppCard backgroundColor="$background" px="$sm" py="$sm">
      <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
        {label}
      </AppText>
      <AppText className="mt-1" fontSize="$xl" fontWeight="700">{value}</AppText>
    </AppCard>
  );
}

export default function FamilyCareReviewSection() {
  const { user } = useApp();
  const [adultDependants, setAdultDependants] = useState<AdultDependantRow[]>([]);
  const [reviews, setReviews] = useState<FamilyCareReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingReview, setEditingReview] = useState<FamilyCareReviewRow | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [adultSelection, setAdultSelection] = useState('');
  const [reviewType, setReviewType] = useState<FamilyCareReviewType>('care-plan-review');
  const [dueDate, setDueDate] = useState('');
  const [reminderDaysBefore, setReminderDaysBefore] = useState(21);
  const [notes, setNotes] = useState('');

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: adultRows }, { data: reviewRows }] = await Promise.all([
      supabase
        .from('adult_dependants')
        .select('*')
        .eq('user_id', user.id)
        .order('name'),
      supabase
        .from('family_care_reviews' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false }),
    ]);

    setAdultDependants((adultRows as AdultDependantRow[]) || []);
    setReviews((reviewRows as FamilyCareReviewRow[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchData();

    const handleRefresh = () => {
      void fetchData();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [fetchData]);

  const namedAdultDependants = useMemo(
    () => adultDependants.filter((adult) => adult.name?.trim().length > 0),
    [adultDependants],
  );

  const resetForm = () => {
    setEditingReview(null);
    setAdultSelection(namedAdultDependants[0]?.id || '');
    setReviewType('care-plan-review');
    setDueDate('');
    setReminderDaysBefore(21);
    setNotes('');
    setShowForm(false);
    setConfirmDeleteId(null);
  };

  const openNewReview = () => {
    setEditingReview(null);
    setAdultSelection(namedAdultDependants[0]?.id || '');
    setReviewType('care-plan-review');
    setDueDate('');
    setReminderDaysBefore(21);
    setNotes('');
    setShowForm(true);
  };

  const openEditReview = (review: FamilyCareReviewRow) => {
    const matchingAdult = namedAdultDependants.find((adult) => adult.id === review.adult_dependant_id);
    setEditingReview(review);
    setAdultSelection(matchingAdult?.id || '__saved__');
    setReviewType((review.review_type as FamilyCareReviewType) || 'care-plan-review');
    setDueDate(review.due_date || '');
    setReminderDaysBefore(review.reminder_days_before ?? 21);
    setNotes(review.notes || '');
    setShowForm(true);
  };

  const selectedAdult = namedAdultDependants.find((adult) => adult.id === adultSelection);
  const selectedAdultName = selectedAdult?.name?.trim()
    || (adultSelection === '__saved__' ? editingReview?.dependant_name?.trim() || '' : '');
  const selectedAdultId = selectedAdult?.id || null;
  const canSave = Boolean(selectedAdultName && dueDate);

  const stats = useMemo(() => {
    const active = reviews.filter((review) => review.status !== 'done').length;
    const dueSoon = reviews.filter((review) => {
      if (review.status === 'done') return false;
      return differenceInDays(parseISO(review.due_date), startOfDay(new Date())) <= review.reminder_days_before;
    }).length;

    return {
      tracked: reviews.length,
      active,
      dueSoon,
    };
  }, [reviews]);

  const handleSave = async () => {
    if (!user || !canSave) return;
    setSaving(true);

    const payload = {
      user_id: user.id,
      adult_dependant_id: selectedAdultId,
      dependant_name: selectedAdultName,
      review_type: reviewType,
      due_date: dueDate,
      reminder_days_before: reminderDaysBefore,
      notes: notes.trim() || null,
    };

    const { error } = editingReview
      ? await supabase.from('family_care_reviews' as any).update(payload).eq('id', editingReview.id)
      : await supabase.from('family_care_reviews' as any).insert(payload);

    setSaving(false);

    if (error) {
      console.error(error);
      toast.error('Could not save that care review right now.');
      return;
    }

    toast.success(editingReview ? 'Care review updated.' : 'Care review saved.');
    notifySectionRefresh();
    resetForm();
    void fetchData();
  };

  const handleDelete = async (review: FamilyCareReviewRow) => {
    const { error } = await supabase.from('family_care_reviews' as any).delete().eq('id', review.id);
    if (error) {
      console.error(error);
      toast.error('Could not remove that care review.');
      return;
    }

    toast.success('Care review removed.');
    setConfirmDeleteId(null);
    notifySectionRefresh();
    void fetchData();
  };

  const updateStatus = async (reviewId: string, status: FamilyCareReviewStatus) => {
    const { error } = await supabase.from('family_care_reviews' as any).update({ status }).eq('id', reviewId);
    if (error) {
      console.error(error);
      toast.error('Could not update that care review right now.');
      return;
    }

    toast.success('Care review updated.');
    notifySectionRefresh();
    void fetchData();
  };

  if (loading) {
    return (
      <AppLoadingState
        title="Loading care reviews"
        body="Pulling the next care, medication, and funding review dates you already saved."
      />
    );
  }

  return (
    <AppCard>
      <AppSectionHeader
        title="Care reviews"
        subtitle="Keep the next care-plan review, carer assessment, medication review, or power-of-attorney admin date visible in one place."
        action={(
          <AppButton
            size="sm"
            onPress={() => (showForm ? resetForm() : openNewReview())}
            disabled={namedAdultDependants.length === 0}
          >
            <Plus size={12} />
            {showForm ? 'Cancel' : 'Add'}
          </AppButton>
        )}
      />

      <div className="grid grid-cols-3 gap-2 mt-4">
        <MiniMetric label="Tracked" value={stats.tracked} />
        <MiniMetric label="Active" value={stats.active} />
        <MiniMetric label="Due soon" value={stats.dueSoon} />
      </div>

      {showForm && (
        <AppCard backgroundColor="$mutedBackground" className="mt-4 animate-fade-in">
          {namedAdultDependants.length === 0 ? (
            <AppEmptyState
              title="Add an adult dependant first"
              body="Save an adult dependant profile first, then keep the next review or carer deadline here."
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppFieldGroup label="Person">
                  <AppNativeSelect
                    value={adultSelection}
                    onChange={(event) => setAdultSelection(event.target.value)}
                  >
                    {namedAdultDependants.map((adult) => (
                      <option key={adult.id} value={adult.id}>
                        {adult.name}
                      </option>
                    ))}
                    {editingReview && !namedAdultDependants.some((adult) => adult.id === editingReview.adult_dependant_id) ? (
                      <option value="__saved__">{editingReview.dependant_name || 'Saved person'}</option>
                    ) : null}
                  </AppNativeSelect>
                </AppFieldGroup>
                <AppFieldGroup label="Review type">
                  <AppNativeSelect
                    value={reviewType}
                    onChange={(event) => setReviewType(event.target.value as FamilyCareReviewType)}
                  >
                    {REVIEW_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppDateField label="Due date" value={dueDate} onChange={setDueDate} />
                <AppFieldGroup label="Remind me">
                  <AppNativeSelect
                    value={String(reminderDaysBefore)}
                    onChange={(event) => setReminderDaysBefore(Number(event.target.value))}
                  >
                    {REMINDER_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {value} days before
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
              </div>

              <AppFieldGroup label="Notes">
                <AppTextArea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  placeholder="What this review is for, who needs to be there, or what to bring."
                />
              </AppFieldGroup>

              <AppButton onPress={handleSave} disabled={!canSave || saving}>
                {saving ? 'Saving...' : 'Save care review'}
              </AppButton>
            </>
          )}
        </AppCard>
      )}

      {namedAdultDependants.length === 0 ? (
        <AppEmptyState
          title="No adult dependant profiles yet"
          body="Add an adult dependant profile first, then use care reviews to keep the next assessment, medication review, or funding check visible."
        />
      ) : reviews.length === 0 ? (
        <AppEmptyState
          title="No care reviews saved yet"
          body="Start with the next medication review, care-plan check-in, or carers assessment you do not want to miss."
        />
      ) : (
        <div className="space-y-3 mt-4">
          {reviews.map((review) => {
            const badge = getReviewBadge(review);

            return (
              <AppCard key={review.id} backgroundColor="$background">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <AppText fontSize="$sm" fontWeight="700">{review.dependant_name}</AppText>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.classes}`}>
                        {badge.text}
                      </span>
                    </div>
                    <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                      {reviewTypeLabel(review.review_type as FamilyCareReviewType)}
                      {review.due_date ? ` · ${formatDate(review.due_date)}` : ''}
                      {review.reminder_days_before ? ` · remind ${review.reminder_days_before} days early` : ''}
                    </AppText>
                    {review.notes ? (
                      <AppText className="mt-2 leading-relaxed" fontSize="$sm" color="$colorMuted">{review.notes}</AppText>
                    ) : null}
                  </div>
                  <AppButton tone="outline" size="sm" onPress={() => openEditReview(review)}>
                    <Pencil size={12} />
                    Edit
                  </AppButton>
                </div>

                <AppButtonRow mt="$sm">
                  {review.status === 'open' ? (
                    <AppButton size="sm" tone="outline" onPress={() => updateStatus(review.id, 'booked')}>
                      Mark booked
                    </AppButton>
                  ) : null}
                  {review.status !== 'done' ? (
                    <AppButton size="sm" tone="outline" onPress={() => updateStatus(review.id, 'done')}>
                      Mark done
                    </AppButton>
                  ) : (
                    <AppButton size="sm" tone="outline" onPress={() => updateStatus(review.id, 'open')}>
                      Reopen
                    </AppButton>
                  )}
                  {confirmDeleteId === review.id ? (
                    <>
                      <AppButton size="sm" tone="destructive" onPress={() => void handleDelete(review)}>
                        Confirm remove
                      </AppButton>
                      <AppButton size="sm" tone="outline" onPress={() => setConfirmDeleteId(null)}>
                        Cancel
                      </AppButton>
                    </>
                  ) : (
                    <AppButton size="sm" tone="outline" onPress={() => setConfirmDeleteId(review.id)}>
                      <Trash2 size={12} />
                      Remove
                    </AppButton>
                  )}
                </AppButtonRow>
              </AppCard>
            );
          })}
        </div>
      )}
    </AppCard>
  );
}
